import React, { useMemo } from 'react';
import { PAYMENTS_FILTER_OPTIONS } from '../resources/enums';
import { numberWithCommas, formatCurrencyAmount, formatNumberValue, showToast } from '../utils/miscFunctions';
import PopOnMe from './elements/poponme';
import { initiateDebiting, refreshLongOverduePayments, refreshMissedPayments, refreshOverduePayments } from '../api/requests';
import Tooltip from './ToolTip';
import { EllipsisVerticalIcon } from '@heroicons/react/24/solid';
import useResizeObserver from '@react-hook/resize-observer';
import { CreditCard, ArrowUp, ArrowDown, Calendar, Clock, AlertTriangle, CheckCircle, Banknote } from 'lucide-react';
import useLayoutHook from '../hooks/layoutHook';
import useUserAccount from '../hooks/userAccountHook';

interface StatusConfig {
  label: string;
  icon: React.ComponentType<any>;
  bgColor: string;
  textColor: string;
  borderColor: string;
  iconBg: string;
}
interface StatusMetrics {
  current: number;
  startOfWeek: number;
}
interface StatusMetricsData {
  [key: string]: StatusMetrics;
}

const statusConfig: Record<keyof typeof PAYMENTS_FILTER_OPTIONS, StatusConfig> = {
  DUE_TODAY: {
    label: 'Due Today',
    icon: Calendar,
    bgColor: 'bg-[#00ADC6]',
    textColor: 'text-white',
    borderColor: 'border-[#00ADC6]',
    iconBg: 'bg-white/20'
  },
  MISSED: {
    label: 'Missed',
    icon: AlertTriangle,
    bgColor: 'bg-[#FFBF00]',
    textColor: 'text-white',
    borderColor: 'border-[#FFBF00]',
    iconBg: 'bg-white/20'
  },
  OVERDUE: {
    label: 'Overdue',
    icon: Clock,
    bgColor: 'bg-[#E8833A]',
    textColor: 'text-white',
    borderColor: 'border-[#E8833A]',
    iconBg: 'bg-white/20'
  },
  LONG_OVERDUE: {
    label: 'Due for Termination',
    icon: AlertTriangle,
    bgColor: 'bg-red-600',
    textColor: 'text-white',
    borderColor: 'border-red-600',
    iconBg: 'bg-white/20'
  },
  DUE_FOR_TERMINATION: {
    label: 'Due for Termination',
    icon: AlertTriangle,
    bgColor: 'bg-red-600',
    textColor: 'text-white',
    borderColor: 'border-red-600',
    iconBg: 'bg-white/20'
  },
  DUE_THIS_WEEK: {
    label: 'This Week',
    icon: Calendar,
    bgColor: 'bg-gray-500',
    textColor: 'text-white',
    borderColor: 'border-gray-500',
    iconBg: 'bg-white/20'
  },
  DUE_THIS_MONTH: {
    label: 'This Month',
    icon: Calendar,
    bgColor: 'bg-gray-500',
    textColor: 'text-white',
    borderColor: 'border-gray-500',
    iconBg: 'bg-white/20'
  },
  OUTSTANDING: {
    label: 'Total Outstanding',
    icon: Banknote,
    bgColor: 'bg-gray-500',
    textColor: 'text-white',
    borderColor: 'border-gray-500',
    iconBg: 'bg-white/20'
  },
  SUCCESS: {
    label: 'Paid',
    icon: CheckCircle,
    bgColor: 'bg-green-500',
    textColor: 'text-white',
    borderColor: 'border-green-500',
    iconBg: 'bg-white/20'
  },
  DUE_LATER: {
    label: 'Due Later',
    icon: Calendar,
    bgColor: 'bg-[#00ADC6]',
    textColor: 'text-white',
    borderColor: 'border-[#00ADC6]',
    iconBg: 'bg-white/20'
  },
  PENDING: {
    label: 'Pending',
    icon: Clock,
    bgColor: 'bg-gray-400',
    textColor: 'text-white',
    borderColor: 'border-gray-400',
    iconBg: 'bg-white/20'
  }
};

export interface IPaymentsSummaryData {
  at_risk: number
  collected: number
  collection_rate: number
  due_for_termination: number;
  outstanding: number;
  missed_due_today: number;
  overdue: number;
  missed: number;
  success: number;
  due_later: number;
  due_today: number;
  long_overdue: number;
  due_this_month: number;
  due_this_week: number;
  expected: number
  start_due_for_termination: number,
  start_due_later_overdue: number,
  start_due_this_month: number,
  start_due_this_week: number,
  start_due_today: number,
  start_missed: number,
  start_outstanding: number,
  start_overdue: number,
  start_success: number,
  start_total_due: number,
  week_of_year: number,
  year: number
}
interface IPaymentSummaryProps {
  data: IPaymentsSummaryData;
  onFilterChange: (filterBy: PAYMENTS_FILTER_OPTIONS) => void;
  currentFilter: PAYMENTS_FILTER_OPTIONS;
  metricsLoading?: boolean;
}

function PaymentsOverdueTabs(props: IPaymentSummaryProps) {
  const { screenWidth } = useLayoutHook()
  const { user } = useUserAccount();
  const target = React.useRef(null);
  const [width, setWidth] = React.useState(0);

  const isActive = useMemo(
    () =>
      (filter: PAYMENTS_FILTER_OPTIONS): boolean => {
        return filter === props.currentFilter;
      },
    [props.currentFilter]
  );

  useResizeObserver(target, (entry) => {
    setWidth(entry.contentRect.width);
  });

  // Create status metrics mapping
  const statusMetrics: StatusMetricsData = useMemo(() => ({
    DUE_TODAY: {
      current: props.data.due_today,
      startOfWeek: props.data.start_due_today
    },
    MISSED: {
      current: props.data.missed,
      startOfWeek: props.data.start_missed
    },
    OVERDUE: {
      current: props.data.overdue,
      startOfWeek: props.data.start_overdue
    },
    LONG_OVERDUE: {
      current: props.data.long_overdue,
      startOfWeek: props.data.start_due_for_termination
    },
    DUE_FOR_TERMINATION: {
      current: props.data.due_for_termination,
      startOfWeek: props.data.start_due_for_termination
    },
    DUE_THIS_WEEK: {
      current: props.data.due_this_week,
      startOfWeek: props.data.start_due_this_week
    },
    DUE_THIS_MONTH: {
      current: props.data.due_this_month,
      startOfWeek: props.data.start_due_this_month
    },
    OUTSTANDING: {
      current: props.data.outstanding,
      startOfWeek: props.data.start_outstanding
    },
    SUCCESS: {
      current: props.data.success,
      startOfWeek: props.data.start_success
    },
    DUE_LATER: {
      current: props.data.due_later,
      startOfWeek: props.data.start_due_later_overdue
    },
    PENDING: {
      current: 0,
      startOfWeek: 0
    }
  }), [props.data]);

  const shouldCollapseTab = (item: string) => {
    if (!props.metricsLoading) {
      switch (item) {
        case 'Due later overdue':
          return width < 2300;
        case 'Paid':
          return width < 1600;
        case 'Total outstanding':
          return width < 1500;
        case 'This month':
          return width < 1250;
        case 'This week':
          return width < 1050;
        case 'Due for termination':
          return width < 850;
        case 'Overdue':
          return width < 600;
        case 'Missed':
          return width < 450;
        case 'Due today':
          return width < 350;
      }
    }
    return false;
  }

  // Helper function to get display label for shouldCollapseTab
  const getDisplayLabel = (key: keyof typeof PAYMENTS_FILTER_OPTIONS): string => {
    switch (key) {
      case 'DUE_LATER':
        return 'Due later overdue';
      case 'SUCCESS':
        return 'Paid';
      case 'OUTSTANDING':
        return 'Total outstanding';
      case 'DUE_THIS_MONTH':
        return 'This month';
      case 'DUE_THIS_WEEK':
        return 'This week';
      case 'LONG_OVERDUE':
        return 'Due for termination';
      case 'DUE_FOR_TERMINATION':
        return 'Due for termination'; // This will be filtered out to avoid duplicates
      case 'OVERDUE':
        return 'Overdue';
      case 'MISSED':
        return 'Missed';
      case 'DUE_TODAY':
        return 'Due today';
      default:
        return statusConfig[key]?.label || key;
    }
  }

  const handleFilterChange = (filter: PAYMENTS_FILTER_OPTIONS) => {
    props.onFilterChange(filter);
  };

  const initiateOverdueDebiting = (category: PAYMENTS_FILTER_OPTIONS) => {
    initiateDebiting(category)
      .then((response: any) => {
        showToast.success(response?.message ?? 'Payment debit successful.');
      })
      .catch((error: any) => {
        showToast.error(error?.description ?? 'Failed to initiate debiting. Please try again later.');
      });
  };

  const refreshPaymentList = (paymentFlag: string) => {
    if (paymentFlag === "Missed" || paymentFlag === "Due today") {
      refreshMissedPayments()
        .then((response: any) => {
          showToast.success(response?.message ?? 'Payment list refreshed successful.');
        })
        .catch((error: any) => {
          showToast.error(error?.description ?? 'Failed to refresh payment list. Please try again later.');
        });
    }

    if (paymentFlag === "Due for termination") {
      refreshLongOverduePayments()
        .then((response: any) => {
          showToast.success(response?.message ?? 'Payment list refreshed successful.');
        })
        .catch((error: any) => {
          showToast.error(error?.description ?? 'Failed to refresh payment list. Please try again later.');
        });
    }

    if (paymentFlag === "Overdue") {
      refreshOverduePayments()
        .then((response: any) => {
          showToast.success(response?.message ?? 'Payment list refreshed successful.');
        })
        .catch((error: any) => {
          showToast.error(error?.description ?? 'Failed to refresh payment list. Please try again later.');
        });
    }

  };

  const getActions = (category: PAYMENTS_FILTER_OPTIONS, paymentFlag?: string) => {
    // Initiate direct charge hidden — wallet-debit flow not used in
    // this release; uncomment when broker→wallet path is re-enabled.
    const actions = [
      // {
      //   label: 'Initiate direct charge',
      //   callback: () => initiateOverdueDebiting(category)
      // },
      {
        label: 'Refresh list',
        callback: () => paymentFlag && refreshPaymentList(paymentFlag)
      }
    ];

    return actions;
  };

  const getMoreTabsActions = () => {
    const actions = [];

    if (shouldCollapseTab('Due later overdue')) {
      actions.push({
        label: `Due later overdue - ${numberWithCommas(props.data.due_later.toFixed(2))}`,
        callback: () => handleFilterChange(PAYMENTS_FILTER_OPTIONS.DUE_LATER)
      })
    }
    if (shouldCollapseTab('Paid')) {
      actions.push({
        label: `Paid - ${numberWithCommas(props.data.success.toFixed(2))}`,
        callback: () => handleFilterChange(PAYMENTS_FILTER_OPTIONS.SUCCESS)
      })
    }
    if (shouldCollapseTab('Total outstanding')) {
      actions.push({
        label: `Total outstanding - ${numberWithCommas(props.data.outstanding.toFixed(2))}`,
        callback: () => handleFilterChange(PAYMENTS_FILTER_OPTIONS.OUTSTANDING)
      })
    }
    if (shouldCollapseTab('This month')) {
      actions.push({
        label: `This month - ${numberWithCommas(props.data.due_this_month.toFixed(2))}`,
        callback: () => handleFilterChange(PAYMENTS_FILTER_OPTIONS.DUE_THIS_MONTH)
      })
    }
    if (shouldCollapseTab('This week')) {
      actions.push({
        label: `This week - ${numberWithCommas(props.data.due_this_week.toFixed(2))}`,
        callback: () => handleFilterChange(PAYMENTS_FILTER_OPTIONS.DUE_THIS_WEEK)
      })
    }
    if (shouldCollapseTab('Due for termination')) {
      actions.push({
        label: `Due for termination - ${numberWithCommas(props.data.due_for_termination.toFixed(2))}`,
        icon: <CreditCard onClick={() => initiateOverdueDebiting(PAYMENTS_FILTER_OPTIONS.LONG_OVERDUE)} className='cursor-pointer h-4 w-4' />,
        iconHasAction: true,
        callback: () => handleFilterChange(PAYMENTS_FILTER_OPTIONS.LONG_OVERDUE)
      })
    }

    if (shouldCollapseTab('Overdue')) {
      actions.push({
        label: `Overdue - ${numberWithCommas(props.data.overdue.toFixed(2))}`,
        icon: <CreditCard onClick={() => initiateOverdueDebiting(PAYMENTS_FILTER_OPTIONS.OVERDUE)} className='cursor-pointer h-4 w-4' />,
        iconHasAction: true,
        callback: () => handleFilterChange(PAYMENTS_FILTER_OPTIONS.OVERDUE)
      })
    }
    if (shouldCollapseTab('Missed')) {
      actions.push({
        label: `Missed - ${numberWithCommas(props.data.missed.toFixed(2))}`,
        icon: <CreditCard onClick={() => initiateOverdueDebiting(PAYMENTS_FILTER_OPTIONS.MISSED)} className='cursor-pointer h-4 w-4' />,
        iconHasAction: true,
        callback: () => handleFilterChange(PAYMENTS_FILTER_OPTIONS.MISSED)
      })
    }

    return actions.reverse();
  };

  return (
    <div ref={target} className="relative py-5">

      <div className="flex">
        {/* Status Metrics Cards */}
        <div className="grid grid-flow-col gap-3 w-full">
          {!user.isBroker() ? (
            (Object.keys(statusConfig) as Array<keyof typeof PAYMENTS_FILTER_OPTIONS>).map((key) => {
              const isTabActive = isActive(PAYMENTS_FILTER_OPTIONS[key as keyof typeof PAYMENTS_FILTER_OPTIONS]);
              const config = statusConfig[key];
              const metrics = statusMetrics[key];
              const IconComponent = config.icon;
              const change = metrics.current - metrics.startOfWeek;
              const changePercent = metrics.startOfWeek > 0
                ? ((change / metrics.startOfWeek) * 100).toFixed(0)
                : metrics.current > 0 ? '100' : '0';
              const isPositiveChange = change > 0;

              // Skip PENDING as it's not used in the current implementation
              if (key === 'PENDING') return null;

              // Skip DUE_FOR_TERMINATION to avoid duplicate with LONG_OVERDUE
              if (key === 'DUE_FOR_TERMINATION') return null;

              // Check if this tab should be collapsed
              const displayLabel = getDisplayLabel(key);
              const shouldHide = shouldCollapseTab(displayLabel);
              if (shouldHide) return null;

              // Get actions for this card
              const paymentFlag = displayLabel === 'Due for termination' ? 'Due for termination' :
                displayLabel === 'Missed' ? 'Missed' :
                  displayLabel === 'Overdue' ? 'Overdue' :
                    displayLabel === 'Due today' ? 'Due today' : undefined;
              const actions = paymentFlag ? getActions(PAYMENTS_FILTER_OPTIONS[key as keyof typeof PAYMENTS_FILTER_OPTIONS], paymentFlag) : undefined;

              return (
                <button
                  key={key}
                  onClick={() => handleFilterChange(PAYMENTS_FILTER_OPTIONS[key as keyof typeof PAYMENTS_FILTER_OPTIONS])}
                  className={`relative flex flex-col p-4 rounded-xl text-left transition-all duration-200 ${isTabActive
                    ? `${config.bgColor} shadow-md border-2 ${config.borderColor}`
                    : `bg-white dark:bg-darkBg border border-gray-200 dark:border-darkBg-border hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm`
                    }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className={`p-1.5 rounded-lg ${isTabActive ? config.iconBg : 'bg-gray-100 dark:bg-darkBg-border-darkBg-border'}`}>
                      <IconComponent className={`w-4 h-4 ${isTabActive ? config.textColor : 'text-gray-500 dark:text-darkBg-subText'}`} />
                    </div>
                    <div className="flex items-center gap-1">
                      <div className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${isTabActive ? `${config.iconBg} ${config.textColor}` : 'bg-gray-100 dark:bg-darkBg-border-darkBg-border text-gray-600 dark:text-darkBg-subText'
                        }`} title="Start of week value">
                        <span>{formatNumberValue(metrics.startOfWeek)}</span>
                      </div>

                      {actions && (
                        <Tooltip bgColor="bg-primary-surface" message={'Actions'}>
                          <PopOnMe actions={actions}>
                            <div className={`p-1 rounded-full cursor-pointer ${isTabActive ? 'hover:bg-white/20' : 'hover:bg-gray-200 dark:hover:bg-gray-600'
                              }`}>
                              <EllipsisVerticalIcon className={`w-3 h-3 ${isTabActive ? 'text-white' : 'text-gray-500 dark:text-gray-400'}`} />
                            </div>
                          </PopOnMe>
                        </Tooltip>
                      )}
                    </div>
                  </div>

                  <span className={`text-xs font-medium truncate ${isTabActive ? config.textColor : 'text-gray-500 dark:text-darkBg-subText'
                    }`}>
                    {config.label}
                  </span>

                  <span className={`mt-1 text-lg font-bold truncate ${isTabActive ? config.textColor : 'text-gray-900 dark:text-darkBg-mainText'
                    }`}>
                    {formatCurrencyAmount(metrics.current)}
                  </span>

                  {/* Change indicator */}
                  {change !== 0 && (
                    <div className={`flex items-center gap-0.5 mt-1 text-xs font-medium ${isPositiveChange ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                      }`}>
                      {isPositiveChange ? (
                        <ArrowUp className="w-3 h-3" />
                      ) : (
                        <ArrowDown className="w-3 h-3" />
                      )}
                      <span>{isPositiveChange ? '+' : ''}{changePercent}%</span>
                    </div>
                  )}

                  {change === 0 && (
                    <div className="flex items-center gap-0.5 mt-1 text-xs font-medium text-gray-400 dark:text-gray-500">
                      <span>No change</span>
                    </div>
                  )}

                  {isTabActive && (
                    <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-1 rounded-t-full ${config.textColor.replace('text', 'bg')}`} />
                  )}
                </button>
              );
            })
          ) : (
            // Broker view - only show OUTSTANDING and SUCCESS
            ([PAYMENTS_FILTER_OPTIONS.OUTSTANDING, PAYMENTS_FILTER_OPTIONS.SUCCESS] as Array<keyof typeof PAYMENTS_FILTER_OPTIONS>).map((key) => {
              const isTabActive = isActive(PAYMENTS_FILTER_OPTIONS[key as keyof typeof PAYMENTS_FILTER_OPTIONS]);
              const config = statusConfig[key];
              const metrics = statusMetrics[key];
              const IconComponent = config.icon;
              const change = metrics.current - metrics.startOfWeek;
              const changePercent = metrics.startOfWeek > 0
                ? ((change / metrics.startOfWeek) * 100).toFixed(0)
                : metrics.current > 0 ? '100' : '0';
              const isPositiveChange = change > 0;

              return (
                <button
                  key={key}
                  onClick={() => handleFilterChange(PAYMENTS_FILTER_OPTIONS[key as keyof typeof PAYMENTS_FILTER_OPTIONS])}
                  className={`relative flex flex-col p-4 rounded-xl text-left transition-all duration-200 ${isTabActive
                    ? `${config.bgColor} shadow-md border-2 ${config.borderColor}`
                    : `bg-white dark:bg-darkBg border border-gray-200 dark:border-darkBg-border hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm`
                    }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className={`p-1.5 rounded-lg ${isTabActive ? config.iconBg : 'bg-gray-100 dark:bg-gray-700'}`}>
                      <IconComponent className={`w-4 h-4 ${isTabActive ? config.textColor : 'text-gray-500 dark:text-gray-400'}`} />
                    </div>
                    <div className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${isTabActive ? `${config.iconBg} ${config.textColor}` : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                      }`} title="Start of week value">
                      <span>{formatNumberValue(metrics.startOfWeek)}</span>
                    </div>
                  </div>
                  <span className={`text-xs font-medium truncate ${isTabActive ? config.textColor : 'text-gray-500 dark:text-gray-400'
                    }`}>
                    {config.label}
                  </span>
                  <span className={`mt-1 text-lg font-bold truncate ${isTabActive ? config.textColor : 'text-gray-900 dark:text-darkBg-mainText'
                    }`}>
                    {formatCurrencyAmount(metrics.current)}
                  </span>
                  {/* Change indicator */}
                  {change !== 0 && (
                    <div className={`flex items-center gap-0.5 mt-1 text-xs font-medium ${isPositiveChange ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                      }`}>
                      {isPositiveChange ? (
                        <ArrowUp className="w-3 h-3" />
                      ) : (
                        <ArrowDown className="w-3 h-3" />
                      )}
                      <span>{isPositiveChange ? '+' : ''}{changePercent}%</span>
                    </div>
                  )}
                  {change === 0 && (
                    <div className="flex items-center gap-0.5 mt-1 text-xs font-medium text-gray-400 dark:text-gray-500">
                      <span>No change</span>
                    </div>
                  )}
                  {isTabActive && (
                    <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-1 rounded-t-full ${config.textColor.replace('text', 'bg')}`} />
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* Actions dropdown for collapsed tabs */}
        {getMoreTabsActions().length > 0 && !user.isBroker() && (
          <div className="mt-4">
            <Tooltip bgColor="bg-primary-surface" message={'Actions'}>
              <PopOnMe actions={getMoreTabsActions()}>
                <div className="rounded-md cursor-pointer px-[2px] py-[4px] flex items-center gap-1">
                  <EllipsisVerticalIcon className="h-5 w-5 text-gray-500" />
                </div>
              </PopOnMe>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
}

export default PaymentsOverdueTabs;
